'use client';

import { useState, useEffect, useRef } from "react";
import styles from "./DropdownTags.module.css";

/**
 * DropdownTags Component
 * ----------------------
 * A dropdown selector that allows users to choose a sorting option (e.g. Popularity, Date, Title).
 * 
 * Props:
 *  - sortBy (string): the currently selected sorting option (displayed on the button)
 *  - onChange (function): callback function called when a new option is selected
 */
export default function DropdownTags({ sortBy, onChange }) {
  // State to manage whether the dropdown is open or closed
  const [isOpen, setIsOpen] = useState(false);

  // Reference to the dropdown container — used to detect outside clicks
  const selectorRef = useRef(null);

  // Toggles the open/closed state of the dropdown
  const toggleOpen = () => setIsOpen(!isOpen);

  /**
   * Effect: Closes the dropdown when the user clicks outside of it.
   * Adds a global mousedown listener when mounted,
   * and cleans it up when the component unmounts.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      {/* Label for accessibility */}
      <label htmlFor="dropdown" className={styles.label}>
        Trier par
      </label>

      {/* Dropdown wrapper (button + menu) */}
      <div className={styles.dropdown} ref={selectorRef} id="dropdown">
        {/* Button that toggles the menu visibility */}
        <button className={styles.selected} onClick={toggleOpen}>
          {sortBy}
          {/* Dropdown arrow icon (rotates when menu is open) */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            className={`${styles.icon} ${isOpen ? styles.open : ""}`}
            fill="white"
          >
            <path d="M201.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 338.7 54.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
          </svg>
        </button>

        {/* Dropdown menu (only rendered when open) */}
        {isOpen && (
          <ul className={styles.menu}>
            {/* Option 1: Popularity */}
            <li
              onClick={() => {
                onChange("Popularité");
                setIsOpen(false);
              }}
            >
              Popularité
            </li>

            {/* Option 2: Date */}
            <li
              onClick={() => {
                onChange("Date");
                setIsOpen(false);
              }}
            >
              Date
            </li>

            {/* Option 3: Title */}
            <li
              onClick={() => {
                onChange("Titre");
                setIsOpen(false);
              }}
            >
              Titre
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}
